import { ServiceBase } from "@app/shared";
import {
  ExceptionsDataModel,
  ExceptionDetailsModel,
  ExceptionsFilterRequestModel,
  ExportVolumeSpecialtyGroupExceptionsModel,
  CreateExceptionsRequestModel,
  BulkExceptionEditModel, ExceptionsPaginationResult
} from "@app/exceptions/interfaces";
import { SupplementaryDocument, UploadBulkExceptionsResponse, UploadBulkExceptionsValidRecord } from "@app/projects/results/common/specialtyGroupException";
import { BulkFilterData } from "@app/projects/results/common/specialtyGroupException";
import { ExportCmsExceptionReportRequestModel } from "@app/exports/exportModels";
import { PaginationResult } from "@app/shared/waffles/exports";
import { clone } from "lodash";

export default class ExceptionsService extends ServiceBase {
  /**
   * @returns a BulkFilterData object containing the project, specialty, and service area
   * filter options for the exceptions Overview and Archived tabs. Each of the objects contains the
   * key-value pairs for the given filter options.
   */
  public async getFilterOptions(getArchived: boolean): Promise<BulkFilterData> {
    return await this.makeGet<BulkFilterData>(`specialtygroupexceptions/bulk-filters?getArchived=${getArchived}`);
  }

  /**
   * @param customProjectId integer representing the custom project id
   * @param exceptionId integer representing the exception id
   * @returns an ExceptionDetailslModel object containing the details of the exception of
   * retrieved from the given custom project id and exception id.
   */
  public async getExceptionDetails(customProjectId: number, exceptionId: number): Promise<ExceptionDetailsModel> {
    return await this.makeGet<ExceptionDetailsModel>(`/specialtygroupexceptions/${customProjectId}/specialtygroupexceptions/${exceptionId}`);
  }

  /**
   * @param requestModel an instance of ExceptionsFilterRequestModel containing the
   * filter options to be used to retrieve the exceptions data.
   * @returns a PaginationResult<ExceptionsDataModel> object containing the standard exceptions data
   * retrieved from the database using the provided filter options and a totalCount
   */
  public async getExceptionsData(requestModel: ExceptionsFilterRequestModel): Promise<PaginationResult<ExceptionsDataModel>> {
    /*We do a subtraction by 1 since the backend handles a zero based indexing,
    and clone the request to avoid updating the context value and getting it assigned to zero at some point */
    const clonedRequest = clone(requestModel);
    clonedRequest.pageIndex--;
    return await this.makePost<ExceptionsPaginationResult<ExceptionsDataModel>>("/specialtygroupexceptions/bulk", clonedRequest);
  }

  /**
   * @returns an ExportVolumeSpecialtyGroupExceptionsModel array containing the
   * volume exceptions data retrieved from the database.
   */
  public async getVolumeExceptionsDownloadReports(): Promise<ExportVolumeSpecialtyGroupExceptionsModel[]> {
    return await this.makeGet<ExportVolumeSpecialtyGroupExceptionsModel[]>('/specialtygroupexceptions/reports');
  }

  /**
   * @param model an instance of ExportCmsExceptionReportRequestModel containing the
   * export options to be used to export the CMS exception report
   * @returns void
   */
  public async exportCmsExceptionReport(model: ExportCmsExceptionReportRequestModel): Promise<void> {
    await this.makePost<void>('/exports/cms-exception-report', model);
  }

  public async uploadSupplementaryDocument(formData: FormData): Promise<number> {
    return await this.makePost<number>('/supplementarydocumentation', formData, undefined, true, false, false);
  }

  /**
   * @param model an instance of CreateExceptionsRequestModel containing the data to be used
   * to create a new exception.
   */
  public async createException(model: CreateExceptionsRequestModel): Promise<void> {
    await this.makePost<void>('/specialtygroupexceptions', model);
  }

  public async addSupplementaryDocumentsToExceptions(models: SupplementaryDocument[]): Promise<void> {
    await this.makePost<void>('/supplementarydocumentation/bulk', models);
  }

  /**
   * @param exceptionIDs a list of SpecialtyGroupException IDs to be archived
   * @returns void
   */
  public async archiveExceptions(exceptionIDs: number[]): Promise<void> {
      return await this.makePatch<void>('/specialtygroupexceptions/archive/bulk', exceptionIDs);
  }

  /**
  * @param specialtyGroupExceptionIds a list of SpecialtyGroupException IDs to be expired
  * @returns void
  */
  public async expireSpecialtyGroupExceptions(specialtyGroupExceptionIds: number[]): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/expire/bulk', specialtyGroupExceptionIds);
  }

  /**
  * @param metadataId document metadata ID to be downloaded
  * @returns the URL to download the metadata ID from.
  */
  public downloadSupplementaryDocument(metadataId: number): string {
    return `${window.apiEndpointV2_0}/supplementarydocumentation/${metadataId}/download`;
  }

  /**
   * @param model an instance of BulkExceptionEditModel containing a list of exception IDs and data to be used
   * to update the corresponding exceptions
   */
  public async bulkUpdateExceptionsByIds(model: BulkExceptionEditModel): Promise<void> {
    await this.makePatch<void>('/specialtygroupexceptions/bulk', model);
  }

  /**
   *
   * @param bulkExceptionsFile
   * @returns a UploadBulkExceptionResponse object
   */
  public async uploadBulkExceptionsFile(bulkExceptionsFile: FormData): Promise<UploadBulkExceptionsResponse> {
    if (!bulkExceptionsFile) {
      return null;
    }

    return await this.makePost<UploadBulkExceptionsResponse>(`specialtygroupexceptions/file/upload-bulk`, bulkExceptionsFile, undefined, true, false, false);
  }

  /**
   *
   * @param model is an array of UploadBulkExceptionsValidRecord objects
   */
  public async bulkCreateExceptions(model: UploadBulkExceptionsValidRecord[]): Promise<void> {
    await this.makePost<void>('/specialtygroupexceptions/bulk/create', model);
  }
}

